import '../WhatsNewModal.scss';

import { useQuery } from '@tanstack/react-query';
import { useServices } from 'index';
import { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { DefaultRoutes } from 'Routes';

import { Button } from '@trackman/web-shared-components';
import { SectionTitle } from 'components/TextComps/SectionTitle';
import { Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { ReactComponent as Close } from 'images/icons/Close.svg';
import { Badge } from 'components/Badge/Badge';

export const WhatsNewSingleStepModal = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const service = useServices().profileService;
  const { data: isUserPremium, isPending } = useQuery({
    queryKey: ['isUserMember'],
    queryFn: service.checkIfUserIsMember,
    refetchOnWindowFocus: false,
  });

  const [isOpen, setIsOpen] = useState(false);
  const whatsNewModalVersion = 'V1.0';

  useEffect(() => {
    if (
      (localStorage.getItem('whatsNewModalVersion') === null || localStorage.getItem('whatsNewModalVersion') !== whatsNewModalVersion) &&
      location.pathname === DefaultRoutes.Home
    ) {
      setIsOpen(true);
    }
  }, [location.pathname]);

  const handleDismiss = () => {
    localStorage.setItem('whatsNewModalVersion', whatsNewModalVersion);
    setIsOpen(false);
  };

  const handleUpgradeToPremium = () => {
    localStorage.setItem('whatsNewModalVersion', whatsNewModalVersion);
    setIsOpen(false);
    navigate(DefaultRoutes.Webshop);
  };

  return (
    <Dialog open={isOpen} onClose={handleDismiss} aria-labelledby='whats-new-modal' className='whats-new-modal single-step-modal'>
      <DialogTitle className='single-step-modal-title'>
        <Close className='close' onClick={handleDismiss} />
      </DialogTitle>
      {isUserPremium ? (
        <PremiumUserContent handleIUnderstand={handleDismiss} />
      ) : (
        <FreeUserContent handleDismiss={handleDismiss} handleUpgradeToPremium={handleUpgradeToPremium} />
      )}
    </Dialog>
  );
};

const PremiumUserContent = ({ handleIUnderstand }: { handleIUnderstand: () => void }) => {
  const { t } = useTranslation();

  return (
    <>
      <DialogContent className='modal-body'>
        <SectionTitle>
          <>{t('TrackmanUniversityPricingUpdate', { ns: 'whatsNewModal' })}</>
        </SectionTitle>
        <div className='whats-new-single-step-description'>
          <div>{t('WeAreUpdatingTrackmanUniversity', { ns: 'whatsNewModal' })}</div>
          <div className='bold'>{t('MovingForward', { ns: 'whatsNewModal' })}</div>
          <ul>
            <li>{t('CertificationsArePurchasedIndividually', { ns: 'whatsNewModal' })}</li>
            <li>
              {t('PremiumMembershipProvidesADiscount', {
                ns: 'whatsNewModal',
                price: '195',
              })}
            </li>
          </ul>
          <div>{t('YourCurrentSubscriptionWillExpire', { ns: 'whatsNewModal' })}</div>
          <div className='bold'>{t('ToContinueEnjoyingPremium', { ns: 'whatsNewModal' })}</div>
        </div>
      </DialogContent>
      <DialogActions className='modal-actions'>
        <Button onClick={handleIUnderstand} label={t('IUnderstand', { ns: 'whatsNewModal' })} intent='primary' />
      </DialogActions>
    </>
  );
};

const FreeUserContent = ({ handleDismiss, handleUpgradeToPremium }: { handleDismiss: () => void; handleUpgradeToPremium: () => void }) => {
  const { t } = useTranslation();

  return (
    <>
      <DialogContent className='modal-body'>
        <Badge badgeText={t('ActionRequired', { ns: 'whatsNewModal' })} className='badge--brandOrange' />
        <SectionTitle>
          <>{t('UnlockToElevateLearning', { ns: 'whatsNewModal' })}</>
        </SectionTitle>
        <div className='whats-new-single-step-description'>
          <div>{t('UpgradeToTrackmanPremium', { ns: 'whatsNewModal' })}</div>
          <div className='bold'>{t('PremiumMembersReceive', { ns: 'whatsNewModal' })}</div>
          <ul>
            <li>{t('50%DiscountOnAllCertifications', { ns: 'whatsNewModal' })}</li>
            <li>
              {t('AccessToExclusiveInsights', {
                ns: 'whatsNewModal',
              })}
            </li>
            <li>{t('InvitationsToTrackmanEvents', { ns: 'whatsNewModal' })}</li>
            <li>{t('VisibilityInTheTrackmanCoachingEcosystem', { ns: 'whatsNewModal' })}</li>
          </ul>
          <div>
            <Trans
              i18nKey='PremiumMembershipCost'
              ns='whatsNewModal'
              components={{ bold: <strong /> }}
              values={{ price: '195' }}
              defaults='Premium membership is <bold>${{price}} per year</bold> and can be joined anytime.'
            ></Trans>
          </div>
        </div>
      </DialogContent>
      <DialogActions className='modal-actions'>
        <Button onClick={handleDismiss} label={t('Dismiss', { ns: 'whatsNewModal' })} />
        <Button onClick={handleUpgradeToPremium} label={t('UpgradeToPremium', { ns: 'whatsNewModal' })} intent='primary' />
      </DialogActions>
    </>
  );
};
